import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import _ from 'lodash';

import { ItemList } from 'Common/Components/ItemList/ItemList';
import { NutritionalHoverConnected } from 'Common/Components/NutritionalHover/NutritionalHover';
import { groupItemsByCategory } from '../utilities';
import { ALACARTE_RECIPE_CATEGORY } from '../constants';

class CycleMenuDay extends Component {
  static propTypes = {
    dayNumber: PropTypes.number.isRequired,
    items: PropTypes.array,
    index: PropTypes.number.isRequired,
    selected: PropTypes.bool,
    copied: PropTypes.bool,
    onClick: PropTypes.func,
    selectMenuItem: PropTypes.func,
    copiedRecipesById: PropTypes.object,
    selectedRecipesById: PropTypes.object,
    disableHover: PropTypes.bool,
    clearSelection: PropTypes.func,
    recipeCategories: PropTypes.array,
    isReadOnly: PropTypes.bool,
    dailyRequirements: PropTypes.array,
    showMealContributionReq: PropTypes.bool,
    openRecipe: PropTypes.func,
  };

  static defaultProps = {
    items: [],
    selected: false,
    onClick: () => {},
    disableHover: true,
    selectMenuItem: () => {},
    selectedItems: [],
    clearSelection: () => {},
    recipeCategories: [],
    isReadOnly: true,
    dailyRequirements: [],
    showMealContributionReq: false,
    openRecipe: () => {},
  };

  onItemClick = (id) => (e) => {
    const { selectedRecipesById, selectMenuItem, clearSelection, isReadOnly } =
      this.props;

    if (isReadOnly) {
      return;
    }

    if (!Object.keys(selectedRecipesById).length) {
      selectMenuItem(id);
    }

    if (Object.keys(selectedRecipesById).length) {
      if (!e.metaKey && !e.ctrlKey) {
        clearSelection();
      }
      selectMenuItem(id);
    }
    e.stopPropagation();
  };

  get itemsByCategory() {
    const { items, recipesById } = this.props;
    return groupItemsByCategory(items, recipesById, true);
  }

  isItemSelected = (id) => !!this.props.selectedRecipesById[id];
  isItemInactive = (inactive) => inactive === 'Y';
  isItemCopied = (id) => !!this.props.copiedRecipesById[id];

  renderItem = (categoryId) => (item, index, array) => {
    const { disableHover, isReadOnly, openRecipe } = this.props;

    return (
      <Draggable
        index={item.OrderNum}
        draggableId={item.draggableId}
        key={index}
        isDragDisabled={
          isReadOnly || categoryId === ALACARTE_RECIPE_CATEGORY.Id
        }
      >
        {(provided, snapshot) => {
          const disabledHover = snapshot.isDragging || disableHover;
          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <NutritionalHoverConnected
                recipeId={item.RecipeId}
                showDisclaimer={false}
                showServingCost={true}
                disableHover={disabledHover}
                openDelay={1000}
                openRecipe={openRecipe}
              >
                <div
                  onClick={this.onItemClick(item.draggableId)}
                  onDoubleClick={() => openRecipe(item.RecipeId)}
                  className={classNames(
                    'menu-cycle-day-item hide-text-selection',
                    {
                      selected: this.isItemSelected(item.draggableId),
                      inactive: this.isItemInactive(item.RecipeInactive),
                      copied: this.isItemCopied(item.draggableId),
                    }
                  )}
                  data-draggable-id={`${item.draggableId}`}
                >
                  {item.Name}
                </div>
              </NutritionalHoverConnected>
              {item.Children.map((child) => {
                return (
                  <NutritionalHoverConnected
                    key={child.draggableId}
                    recipeId={child.RecipeId}
                    showDisclaimer={false}
                    showServingCost={true}
                    disableHover={disabledHover}
                    openDelay={1000}
                    openRecipe={openRecipe}
                  >
                    <div
                      onClick={this.onItemClick(child.draggableId)}
                      onDoubleClick={() => openRecipe(child.RecipeId)}
                      className={classNames(
                        'menu-cycle-day-item hide-text-selection child',
                        {
                          selected: this.isItemSelected(child.draggableId),
                          inactive: this.isItemInactive(child.RecipeInactive),
                          copied: this.isItemCopied(child.draggableId),
                        }
                      )}
                      data-draggable-id={`${child.draggableId}`}
                    >
                      {child.Name}
                    </div>
                  </NutritionalHoverConnected>
                );
              })}
            </div>
          );
        }}
      </Draggable>
    );
  };

  renderCategory = (category, index) => {
    const { dayNumber } = this.props;
    const items = this.itemsByCategory[category.Id] || [];

    const parentItems = items.filter((i) => i.level === 0);

    if (parentItems.length === 0) {
      return null;
    }

    const orderedItems = _.sortBy(parentItems, 'OrderNum', 'asc');
    const firstItemInCategory = orderedItems[0];
    return (
      <div key={category.Id} className="menu-calendar-category-list">
        <ItemList
          renderHeader={
            <Draggable
              index={firstItemInCategory.OrderNum - 1}
              draggableId={`${dayNumber}-${category.Id}`}
              isDragDisabled={true}
            >
              {(provided, snapshot) => {
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <span className="menu-calendar-category-header hide-text-selection">
                      {category.Description}
                    </span>
                  </div>
                );
              }}
            </Draggable>
          }
          items={orderedItems}
          renderItem={this.renderItem(category.Id)}
        />
      </div>
    );
  };

  renderDailyRequirements() {
    const { dailyRequirements, showMealContributionReq } = this.props;

    if (!showMealContributionReq) {
      return null;
    }

    return dailyRequirements.map((req, i) => (
      <div
        className="hide-text-selection menu-calendar-day-requirement"
        key={i}
      >
        {req}
      </div>
    ));
  }

  onDayClick = (e) => {
    const { onClick, isReadOnly, dayNumber } = this.props;

    if (isReadOnly) {
      return;
    }

    onClick(dayNumber);
  };

  render() {
    const {
      dayNumber,
      index,
      selected,
      copied,
      recipeCategories,
      isReadOnly,
      dailyRequirements,
      showMealContributionReq,
    } = this.props;

    const calendarDayClass = classNames('menu-calendar-day', {
      selected,
      copied,
      interactable: !isReadOnly,
      'unmet-requirements':
        showMealContributionReq && dailyRequirements.length > 0,
      'met-requirements':
        showMealContributionReq && dailyRequirements.length < 1,
    });

    return (
      <Droppable
        droppableId={`${dayNumber}`}
        key={dayNumber}
        index={index}
        isDropDisabled={isReadOnly}
      >
        {(provided, snapshot) => {
          return (
            <div
              ref={provided.innerRef}
              className={calendarDayClass}
              onClick={this.onDayClick}
            >
              {/* <div className="menu-calendar-day-header" /> */}
              <ItemList
                items={recipeCategories}
                renderItem={this.renderCategory}
              />
              {provided.placeholder}
              {this.renderDailyRequirements()}
            </div>
          );
        }}
      </Droppable>
    );
  }
}

export { CycleMenuDay };



// WEBPACK FOOTER //
// ./src/pages/managemenu/subpages/editmenu/components/CycleMenuDay.js